import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useCapturedParams } from "../lib/cleanUrl";

/**
 * Password reset page — consumes a one-time token issued by
 * POST /api/auth/forgot-password. Edwin (or any user) lands here via the
 * URL surfaced in the notifications outbox and sets a fresh password.
 *
 * Token is single-use, expires 1 hour after issuance. Backend enforces
 * min 12 chars.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const tokenFromUrl = useCapturedParams().token || "";
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const pwOk = useMemo(() => password.length >= 12 && password === confirm, [password, confirm]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing reset token. Use the link from the reset email."); return; }
    if (password.length < 12) { setErr("Password must be at least 12 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-erf-cream px-4">
        <div className="max-w-md w-full bg-white border border-erf-borderCream rounded-md p-8 text-center"
             data-testid="reset-success">
          <div className="text-erf-gold font-mono text-xs uppercase tracking-widest mb-2">Reset complete</div>
          <h1 className="font-display font-semibold text-2xl text-erf-dark mb-3">Password updated.</h1>
          <p className="text-sm text-erf-dark/70 font-nav">
            Redirecting you to sign in…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-erf-cream px-4 py-12">
      <div className="max-w-md w-full bg-white border border-erf-borderCream rounded-md p-8"
           data-testid="reset-password-form">
        <div className="text-erf-gold font-mono text-[10px] uppercase tracking-widest mb-2">
          ERF ConstructionOS
        </div>
        <h1 className="font-display font-semibold text-2xl text-erf-dark mb-2">
          Set a new password
        </h1>
        <p className="text-xs text-erf-dark/60 font-nav mb-6">
          Enter your new password twice. Minimum 12 characters. This link expires 1 hour after it was issued and can only be used once.
        </p>

        <form onSubmit={submit} className="space-y-4">
          {!tokenFromUrl && (
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-erf-dark/60 block mb-1">
                Reset token
              </label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="w-full border border-erf-borderCream rounded-sm px-3 py-2 font-mono text-xs"
                data-testid="reset-token-input"
                autoComplete="off"
              />
            </div>
          )}
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-erf-dark/60 block mb-1">
              New password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-erf-borderCream rounded-sm px-3 py-2 font-nav"
              data-testid="reset-password-input"
              autoComplete="new-password"
              autoFocus
              minLength={12}
              required
            />
            <div className="text-[10px] font-mono text-erf-dark/40 mt-1">
              {password.length}/12+ characters
            </div>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-erf-dark/60 block mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-erf-borderCream rounded-sm px-3 py-2 font-nav"
              data-testid="reset-confirm-input"
              autoComplete="new-password"
              required
            />
            {confirm && password !== confirm && (
              <div className="text-[10px] font-mono text-red-700 mt-1">Passwords don't match</div>
            )}
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2 font-nav"
                 data-testid="reset-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !pwOk}
            className="w-full bg-erf-gold hover:bg-erf-goldHover text-white font-nav font-semibold py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="reset-submit"
          >
            {busy ? "Setting new password…" : "Set new password"}
          </button>
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-xs font-nav text-erf-dark/60 hover:text-erf-dark underline"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
